import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useGoToTemplate } from "@hooks/useGoToTemplate";
import {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import { ServiceTemplateSchedule } from "@common/types/serviceTemplateSchedule";
import { UserBase } from "@common/types/user";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import { EngagementTeamRole } from "@common/types/taskTemplateAssigneeRole";
import {
    ServiceTemplateSchedulesSortValue,
    ServiceTemplateSchedulesView,
} from "@common/types/views/serviceTemplateSchedulesView";
import { View } from "@common/types/views/views";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Button } from "@design-system/Button/Button";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { HStack } from "@design-system/HStack/HStack";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    useDeleteServiceTemplateScheduleMutation,
    useServiceTemplateSchedules,
    useUpdateServiceTemplateScheduleStatusMutation,
} from "@api/serviceTemplateSchedule.api";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useWorkspacesList } from "@api/workspace.api";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import {
    serviceTemplateSchedulesViewConfig,
    groupServiceTemplateSchedules,
    ServiceTemplateScheduleEntityInfo,
    ServiceTemplateScheduleLeafRow,
    ServiceTemplateScheduleRow,
} from "./serviceTemplateSchedulesViewConfig";
import { ServiceTemplateScheduleOccurrencesDrawer } from "./ServiceTemplateScheduleOccurrencesDrawer";
import { useServiceTemplateScheduleBulkActions } from "./useServiceTemplateScheduleBulkActions";
import {
    getServiceTemplateScheduleDisplayStatus,
    ServiceTemplateScheduleDisplayStatus,
} from "./serviceTemplateScheduleDisplayStatus";
import { ServiceTemplateSchedulesEmptyState } from "./ServiceTemplateSchedulesEmptyState";
import { TemplateEstimateStats } from "./templateEstimateStats";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

export interface ServiceTemplateScheduleCallbacks {
    onDeleteSchedule: (scheduleId: string) => void;
    onPauseSchedule: (scheduleId: string) => void;
    onResumeSchedule: (scheduleId: string) => void;
    onGoToTemplate: (templateId: string) => void;
    onOpenSchedule: (scheduleId: string) => void;
}

/** Member fields needed by the engagement team cell to render an avatar + name, shared between the member lookup map and the cell. */
export type EngagementTeamMember = Pick<
    UserBase,
    "id" | "name" | "preferredName" | "avatar" | "iconColor" | "email"
>;

/** Client (workspace) fields needed to render the client icon + name, keyed by workspace id so schedule rows (which only carry `workspaceId`) can look them up. */
export interface ServiceTemplateScheduleWorkspaceInfo {
    id: string;
    name: string;
    iconColor: string | null;
}

export interface ServiceTemplateScheduleTableOptions {
    entityInfoByUuid: Map<string, ServiceTemplateScheduleEntityInfo>;
    workspaceInfoByUuid: Map<string, ServiceTemplateScheduleWorkspaceInfo>;
    isUpdatingStatus: boolean;
    /** Template ids that are archived, so the status cell can infer the "archived" display status (schedules never persist an archived status themselves — see PRO-10571). */
    archivedTemplateIds: Set<string>;
    /** Template display name keyed by template id, so the template-name cell can resolve a name without each row re-subscribing to `useCloseTemplates`. */
    templateNameById: Map<string, string>;
    /** False until the org's workspaces have loaded at least once. Guards the status cell against treating every schedule as "deleted" while `entityInfoByUuid` is still empty on first render (see PRO-10571). */
    isWorkspacesFetched: boolean;
    /** Organization members keyed by user id, used by the engagement team cell to resolve member names and avatars. */
    memberById: Map<number, EngagementTeamMember>;
    /**
     * Per-template estimate aggregates derived from template tasks
     * (`listCloseTemplates` stats). Never stored on the schedule itself.
     */
    templateEstimateById: Map<string, TemplateEstimateStats>;
}

interface Props {
    view: ServiceTemplateSchedulesView;
    onViewChange: (view: View) => void;
    search?: string;
    /** Restricts the list to schedules for a single template, e.g. when shown from the close template's schedules drawer. */
    templateId?: string;
    /** Filters overlay: Service (close template) multi-select — distinct from the single-template `templateId` scope above. */
    templateIds?: string[];
    /** Filters rows by the computed display status (active/paused/archived/deleted). Empty means no filtering — every status is shown. */
    statusFilter?: ServiceTemplateScheduleDisplayStatus[];
    /** Active backend sort; the list is returned pre-sorted (no FE re-sort). */
    sort?: ServiceTemplateSchedulesSortValue;
    onSortChange?: (sort: ServiceTemplateSchedulesSortValue) => void;
    onSortRemove?: () => void;
    /** Filters overlay: recurrence period multi-select. */
    periods?: ServiceTemplateRecurrencePeriod[];
    /** Filters overlay: engagement team roles/members/operators. */
    engagementTeamRoles?: EngagementTeamRole[];
    engagementTeamRoleOperator?: "any" | "every";
    engagementTeamMemberIds?: number[];
    engagementTeamMemberOperator?: "any" | "every";
    /** Filters overlay: Client (workspace) multi-select. */
    clientIds?: string[];
    /** Controlled nesting-expansion state, shared with the page's expand/collapse-all control. */
    expanded?: ExpandedState;
    onExpandedChange?: OnChangeFn<ExpandedState>;
    tableRef?: React.RefObject<ViewTableHandle | null>;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
}

export const ServiceTemplateSchedulesList = ({
    view,
    onViewChange,
    search,
    templateId,
    templateIds,
    statusFilter,
    sort,
    onSortChange,
    onSortRemove,
    periods,
    engagementTeamRoles,
    engagementTeamRoleOperator,
    engagementTeamMemberIds,
    engagementTeamMemberOperator,
    clientIds,
    expanded: controlledExpanded,
    onExpandedChange: controlledOnExpandedChange,
    tableRef: externalTableRef,
    hasActiveFilters,
    onClearFilters,
}: Props) => {
    const internalTableRef = useRef<ViewTableHandle>(null);
    const tableRef = externalTableRef ?? internalTableRef;
    const { organizationId } = useScope();
    const navigate = useStateAwareNavigate();
    const { toast: successToast, error: errorToast } = useToaster();
    const { mutateAsync: deleteSchedule, isPending: isDeletingSchedule } =
        useDeleteServiceTemplateScheduleMutation();
    const {
        mutateAsync: updateScheduleStatus,
        isPending: isUpdatingScheduleStatus,
    } = useUpdateServiceTemplateScheduleStatusMutation();

    const { data: organizationMembers = [] } = useCurrentOrganizationMembers();

    const memberById = useMemo(() => {
        const map = new Map<number, EngagementTeamMember>();
        for (const member of organizationMembers) {
            map.set(member.user.id, {
                id: member.user.id,
                name: member.user.name,
                preferredName: member.user.preferredName,
                avatar: member.user.avatar,
                iconColor: member.user.iconColor,
                email: member.user.email,
            });
        }
        return map;
    }, [organizationMembers]);

    const {
        data: schedules,
        isFetched,
        isError,
    } = useServiceTemplateSchedules({
        organizationId,
        templateId,
        templateIds,
        search,
        periods,
        engagementTeamRoles,
        engagementTeamRoleOperator,
        engagementTeamMemberIds,
        engagementTeamMemberOperator,
        clientIds,
        sort,
    });

    const { data: workspaces = [], isFetched: isWorkspacesFetched } =
        useWorkspacesList(
            organizationId
                ? { organizationId, includeEntities: true }
                : undefined,
        );

    // Fetches every template (including archived ones) purely to know which
    // templateIds are archived — the status cell/filter infer a schedule's
    // "archived" display status from its template's archivedAt, since the
    // schedule itself never persists an archived status (see PRO-10571).
    const { data: closeTemplates = [], isFetched: isTemplatesFetched } =
        useCloseTemplates({
            includeArchived: true,
        });
    const archivedTemplateIds = useMemo(() => {
        const ids = new Set<string>();
        for (const template of closeTemplates) {
            if (template.archivedAt != null) {
                ids.add(template.id);
            }
        }
        return ids;
    }, [closeTemplates]);

    const templateNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const template of closeTemplates) {
            map.set(template.id, template.name);
        }
        return map;
    }, [closeTemplates]);

    const templateEstimateById = useMemo(() => {
        const map = new Map<string, TemplateEstimateStats>();
        for (const template of closeTemplates) {
            map.set(template.id, {
                totalEstimatedTime: template.totalEstimatedTime,
                estimatedTimeByRole: template.estimatedTimeByRole,
            });
        }
        return map;
    }, [closeTemplates]);

    const entityInfoByUuid = useMemo(() => {
        const map = new Map<string, ServiceTemplateScheduleEntityInfo>();
        for (const workspace of workspaces) {
            for (const entity of workspace.entities ?? []) {
                if (!entity.uuid) {
                    continue;
                }
                map.set(entity.uuid, {
                    id: entity.id,
                    uuid: entity.uuid,
                    name: entity.name ?? "-",
                    iconColor: entity.iconColor,
                    logo: entity.logo,
                    workspaceId: entity.workspaceId,
                });
            }
        }
        return map;
    }, [workspaces]);

    const workspaceInfoByUuid = useMemo(() => {
        const map = new Map<string, ServiceTemplateScheduleWorkspaceInfo>();
        for (const workspace of workspaces) {
            map.set(workspace.id, {
                id: workspace.id,
                name: workspace.name,
                iconColor: workspace.iconColor,
            });
        }
        return map;
    }, [workspaces]);

    // Applies the status filter (if any) to the flat schedule list before
    // grouping, so a filtered-out schedule never contributes to a group row's
    // synthetic entity/client aggregation either. Skipped entirely until both
    // the workspaces and templates lists have loaded — otherwise every
    // schedule would transiently resolve as "deleted" (entityInfoByUuid starts
    // empty) or fail the "archived" lookup (archivedTemplateIds starts empty),
    // filtering out valid rows for one render.
    const filteredSchedules = useMemo(() => {
        if (
            !schedules ||
            !statusFilter ||
            statusFilter.length === 0 ||
            !isWorkspacesFetched ||
            !isTemplatesFetched
        ) {
            return schedules;
        }
        return schedules.filter((schedule) => {
            const displayStatus = getServiceTemplateScheduleDisplayStatus({
                status: schedule.status,
                isTemplateArchived: archivedTemplateIds.has(
                    schedule.templateId,
                ),
                isClientDeleted: !entityInfoByUuid.has(schedule.entityUuid),
            });
            return statusFilter.includes(displayStatus);
        });
    }, [
        schedules,
        statusFilter,
        archivedTemplateIds,
        entityInfoByUuid,
        isWorkspacesFetched,
        isTemplatesFetched,
    ]);

    const rows = useMemo<ServiceTemplateScheduleRow[] | null>(
        () =>
            filteredSchedules
                ? groupServiceTemplateSchedules(filteredSchedules)
                : null,
        [filteredSchedules],
    );

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const clearSelection = useCallback(() => setRowSelection({}), []);

    // Bulk actions only act on leaf schedules. Parent group selection cascades
    // to children via TanStack; we resolve selected IDs back to leaf rows here.
    const rowsById = useMemo(() => {
        const map = new Map<string, ServiceTemplateScheduleLeafRow>();
        for (const row of rows ?? []) {
            if (row.rowType === "schedule") {
                map.set(row.id, row);
            } else {
                for (const child of row.children) {
                    map.set(child.id, child);
                }
            }
        }
        return map;
    }, [rows]);

    const selectedRows = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => rowsById.get(id))
                .filter((row): row is ServiceTemplateScheduleLeafRow => !!row),
        [rowSelection, rowsById],
    );

    // Always render the selection checkbox column so bulk actions are
    // discoverable, regardless of the cached view's persisted options.
    // `selection: true` is required so selected rows get `clickable-row`,
    // which is what makes `.bulk-selected` checkboxes visible (opacity: 1).
    const viewWithBulkActions = useMemo<ServiceTemplateSchedulesView>(
        () => ({
            ...view,
            options: {
                ...view.options,
                selection: true,
                bulkActions: true,
            },
        }),
        [view],
    );

    // ViewTable's onViewChange fires with whatever `view` it's currently
    // rendering (i.e. viewWithBulkActions), so strip the injected overrides
    // before forwarding to the caller — otherwise they would get persisted
    // into the cached view on every resize/sort/column change.
    const handleViewChange = useCallback(
        (newView: View) => {
            onViewChange({
                ...newView,
                options: {
                    ...newView.options,
                    selection: false,
                    bulkActions: false,
                },
            });
        },
        [onViewChange],
    );

    const [occurrencesScheduleId, setOccurrencesScheduleId] = useState<
        string | null
    >(null);

    const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(
        null,
    );

    const schedulesById = useMemo(
        () => new Map((schedules ?? []).map((s) => [s.id, s])),
        [schedules],
    );

    const selectedSchedule = useMemo<ServiceTemplateSchedule | null>(
        () =>
            occurrencesScheduleId
                ? (schedulesById.get(occurrencesScheduleId) ?? null)
                : null,
        [occurrencesScheduleId, schedulesById],
    );

    const [uncontrolledExpanded, setUncontrolledExpanded] =
        useState<ExpandedState>({});
    const expanded = controlledExpanded ?? uncontrolledExpanded;
    const setExpanded = controlledOnExpandedChange ?? setUncontrolledExpanded;

    // Drops expansion keys for group rows that filtering/search has removed
    // from the current row set. TanStack retains expanded ids after their rows
    // disappear, which would otherwise leave the shared expand/collapse-all
    // control reading a non-empty state and firing "Collapse all" on an
    // already-collapsed table — so the first click would only clear stale
    // state instead of expanding the visible groups (see PRO-10827 review).
    useEffect(() => {
        if (!rows || expanded === true) {
            return;
        }
        const expandableRowIds = new Set(
            rows.filter((row) => row.rowType === "group").map((row) => row.id),
        );
        const hasStaleKeys = Object.keys(expanded).some(
            (rowId) => !expandableRowIds.has(rowId),
        );
        if (!hasStaleKeys) {
            return;
        }
        setExpanded(
            Object.fromEntries(
                Object.entries(expanded).filter(([rowId]) =>
                    expandableRowIds.has(rowId),
                ),
            ),
        );
    }, [rows, expanded, setExpanded]);

    const handleShowOccurrences = useCallback((scheduleId: string) => {
        setOccurrencesScheduleId(scheduleId);
    }, []);

    const handleRowClick = useCallback(
        (row?: ServiceTemplateScheduleRow) => {
            if (row && row.rowType === "schedule") {
                handleShowOccurrences(row.id);
            }
        },
        [handleShowOccurrences],
    );

    const handleRequestDeleteSchedule = useCallback((scheduleId: string) => {
        setDeletingScheduleId(scheduleId);
    }, []);

    const handleGoToTemplates = useCallback(() => {
        navigate("/templates/services");
    }, [navigate]);

    const handleGoToTemplate = useGoToTemplate();

    const changeScheduleStatus = useCallback(
        async ({
            scheduleId,
            status,
            successMessage,
            errorMessage,
        }: {
            scheduleId: string;
            status: ServiceTemplateRecurrenceStatus;
            successMessage: string;
            errorMessage: string;
        }) => {
            if (!organizationId || isUpdatingScheduleStatus) {
                return;
            }
            try {
                await updateScheduleStatus({
                    organizationId,
                    scheduleId,
                    status,
                });
                successToast(successMessage);
            } catch {
                errorToast(errorMessage);
            }
        },
        [
            errorToast,
            isUpdatingScheduleStatus,
            organizationId,
            successToast,
            updateScheduleStatus,
        ],
    );

    const handlePauseSchedule = useCallback(
        (scheduleId: string) =>
            changeScheduleStatus({
                scheduleId,
                status: ServiceTemplateRecurrenceStatus.PAUSED,
                successMessage: "Schedule paused",
                errorMessage: "Failed to pause schedule",
            }),
        [changeScheduleStatus],
    );

    const handleResumeSchedule = useCallback(
        (scheduleId: string) =>
            changeScheduleStatus({
                scheduleId,
                status: ServiceTemplateRecurrenceStatus.ACTIVE,
                successMessage: "Schedule resumed",
                errorMessage: "Failed to resume schedule",
            }),
        [changeScheduleStatus],
    );

    const handleConfirmDeleteSchedule = useCallback(async () => {
        if (!organizationId || !deletingScheduleId || isDeletingSchedule) {
            return;
        }
        try {
            await deleteSchedule({
                organizationId,
                scheduleId: deletingScheduleId,
            });
            successToast("Schedule deleted");
            setDeletingScheduleId(null);
        } catch {
            errorToast("Failed to delete schedule");
        }
    }, [
        deleteSchedule,
        deletingScheduleId,
        errorToast,
        isDeletingSchedule,
        organizationId,
        successToast,
    ]);

    const tableMeta = useMemo<
        ViewTableMeta<
            ServiceTemplateScheduleCallbacks,
            ServiceTemplateScheduleTableOptions
        >
    >(
        () => ({
            callbacks: {
                onDeleteSchedule: handleRequestDeleteSchedule,
                onPauseSchedule: handlePauseSchedule,
                onResumeSchedule: handleResumeSchedule,
                onGoToTemplate: handleGoToTemplate,
                onOpenSchedule: handleShowOccurrences,
            },
            options: {
                entityInfoByUuid,
                workspaceInfoByUuid,
                isUpdatingStatus: isUpdatingScheduleStatus,
                archivedTemplateIds,
                templateNameById,
                isWorkspacesFetched,
                memberById,
                templateEstimateById,
            },
        }),
        [
            handleRequestDeleteSchedule,
            handlePauseSchedule,
            handleResumeSchedule,
            handleGoToTemplate,
            handleShowOccurrences,
            entityInfoByUuid,
            workspaceInfoByUuid,
            isUpdatingScheduleStatus,
            archivedTemplateIds,
            templateNameById,
            isWorkspacesFetched,
            memberById,
            templateEstimateById,
        ],
    );

    const {
        handleOpenBulkActions,
        showDeleteConfirm: showBulkDeleteConfirm,
        setShowDeleteConfirm: setShowBulkDeleteConfirm,
        handleBulkDeleteConfirm,
        deleteEligibleCount: bulkDeleteEligibleCount,
        isBulkActionPending,
    } = useServiceTemplateScheduleBulkActions({
        organizationId,
        selectedRows,
        clearSelection,
    });

    const selectedCount = selectedRows.length;

    return (
        <>
            <ViewTable
                ref={tableRef}
                // Gated on every lookup the row cells read (templateNameById,
                // entityInfoByUuid/workspaceInfoByUuid), not just the schedules
                // query itself — otherwise rows render while those maps are
                // still empty and the Template/Client/Entity cells fall back to
                // "-" until an unrelated re-render (e.g. a refresh) happens to
                // land after the lookups resolve. See PRO-10674.
                data={
                    isFetched && isWorkspacesFetched && isTemplatesFetched
                        ? (rows ?? [])
                        : null
                }
                view={viewWithBulkActions}
                config={serviceTemplateSchedulesViewConfig}
                onViewChange={handleViewChange}
                sort={sort}
                onSortChange={onSortChange}
                onSortRemove={onSortRemove}
                expanded={expanded}
                onExpandedChange={setExpanded}
                emptyView={() => (
                    <ServiceTemplateSchedulesEmptyState
                        isError={isError}
                        hasActiveFilters={hasActiveFilters}
                        onClearFilters={onClearFilters}
                        onGoToTemplates={handleGoToTemplates}
                    />
                )}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                onRowClick={handleRowClick}
                fixedTopOffsetPx
                enableColumnResizing
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                isParentSelectable
                footer={
                    <CommandIsland
                        sticky
                        className={commandIslandStyles.bulkActionsStickyFooter}
                    >
                        <HStack gap={8}>
                            {(filteredSchedules ?? []).length > 0 ? (
                                <CommandIslandTotal>
                                    {pluralizeWithCount(
                                        "schedule",
                                        (filteredSchedules ?? []).length,
                                    )}
                                </CommandIslandTotal>
                            ) : undefined}

                            {selectedCount > 0 && (
                                <BulkSelectionControl
                                    size="md"
                                    selectedCount={selectedCount}
                                    onClearSelection={clearSelection}
                                />
                            )}
                        </HStack>

                        {selectedCount > 0 && (
                            <Tooltip content="Open bulk actions">
                                <Button
                                    variant="primary"
                                    size="md"
                                    rightIcon={
                                        <span data-slot-box="">
                                            <Shortcut keys="mod+k" />
                                        </span>
                                    }
                                    onClick={handleOpenBulkActions}
                                    disabled={isBulkActionPending}
                                >
                                    Actions
                                </Button>
                            </Tooltip>
                        )}
                    </CommandIsland>
                }
            />

            <ServiceTemplateScheduleOccurrencesDrawer
                open={!!occurrencesScheduleId}
                schedule={selectedSchedule}
                onClose={() => setOccurrencesScheduleId(null)}
            />

            <ConfirmationModal
                show={!!deletingScheduleId}
                title="Delete schedule?"
                question="This schedule and its upcoming occurrences will be permanently removed."
                yes="Delete"
                no="Cancel"
                onReject={() => setDeletingScheduleId(null)}
                onConfirm={handleConfirmDeleteSchedule}
            />

            <ConfirmationModal
                show={showBulkDeleteConfirm}
                title={`Delete ${pluralizeWithCount("schedule", bulkDeleteEligibleCount)}?`}
                question={`This will permanently remove the selected ${pluralize("schedule", bulkDeleteEligibleCount)} and their upcoming occurrences. This action cannot be undone.`}
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onConfirm={handleBulkDeleteConfirm}
                onReject={() => setShowBulkDeleteConfirm(false)}
                onHide={() => setShowBulkDeleteConfirm(false)}
            />
        </>
    );
};
